import { CSSProperties } from "react";

/**
 * LogoLoader
 * -----------
 * Reusable brand loader that traces the company logo mark in a continuous
 * sequential motion (mirroring the 1 → 7 build-up direction of the brand's
 * triangular path). Designed for B2B SaaS contexts: calm, premium, restrained.
 *
 * Usage:
 *   <LogoLoader />                        // default (medium)
 *   <LogoLoader size={64} />              // custom pixel size
 *   <LogoLoader label="Loading projects" /> // accessible label + visible caption
 *   <LogoLoader variant="inline" />       // no caption, tight spacing
 *   <LogoLoader fullscreen />             // centered overlay
 */

export interface LogoLoaderProps {
  /** Pixel size of the logo mark (square). Default 56. */
  size?: number;
  /** Accessible label, also shown as caption unless variant="inline". */
  label?: string;
  /** Layout variant. */
  variant?: "default" | "inline" | "fullscreen";
  /** Optional className for the outer wrapper. */
  className?: string;
  /** Optional inline style for the outer wrapper. */
  style?: CSSProperties;
}

const BRAND_COLOR = "#242C63";

const LogoLoader = ({
  size = 56,
  label = "Loading",
  variant = "default",
  className = "",
  style,
}: LogoLoaderProps) => {
  const isFullscreen = variant === "fullscreen";
  const isInline = variant === "inline";

  const wrapperClass = [
    isFullscreen
      ? "fixed inset-0 z-50 flex items-center justify-center bg-white/80"
      : "flex items-center justify-center",
    isInline ? "gap-2" : "flex-col gap-3",
    className,
  ].join(" ");

  return (
    <div
      className={wrapperClass}
      style={style}
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <svg
        width={size}
        height={size * (1119.45 / 1331.29)}
        viewBox="0 0 1331.29 1119.45"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        style={{ display: "block" }}
      >
        {/* Faint static outline so the mark is always perceivable */}
        <g fill="none" stroke={BRAND_COLOR} strokeOpacity={0.12} strokeWidth={14}>
          <path d="M243.57,1004.79l465.62-653.69c37.59-50.95,26.75-122.71-24.2-160.3L0,1119.45h1026.42c78.45,0,142.04-36.19,142.06-114.63l-924.9-.02Z" />
          <path d="M929.72,61.06c-11.11-27.72-48.9-80.2-104.68-53.89-60.66,28.6-40.15,97.86-22.37,141.97,39.47,97.93,365.79,855.65,365.79,855.65h162.85L929.72,61.06Z" />
        </g>

        {/* Animated trace — strokes draw on, then fade as fill resolves */}
        <g
          fill={BRAND_COLOR}
          stroke={BRAND_COLOR}
          strokeWidth={14}
          strokeLinejoin="round"
          strokeLinecap="round"
        >
          <path
            className="logo-loader-path logo-loader-path--main"
            d="M243.57,1004.79l465.62-653.69c37.59-50.95,26.75-122.71-24.2-160.3L0,1119.45h1026.42c78.45,0,142.04-36.19,142.06-114.63l-924.9-.02Z"
          />
          <path
            className="logo-loader-path logo-loader-path--accent"
            d="M929.72,61.06c-11.11-27.72-48.9-80.2-104.68-53.89-60.66,28.6-40.15,97.86-22.37,141.97,39.47,97.93,365.79,855.65,365.79,855.65h162.85L929.72,61.06Z"
          />
        </g>
      </svg>

      {!isInline && (
        <span
          style={{
            fontSize: "0.8125rem",
            fontWeight: 500,
            color: "#64748B",
            letterSpacing: "0.01em",
            fontFamily:
              "'Inter Tight', Inter, system-ui, -apple-system, sans-serif",
          }}
        >
          {label}
        </span>
      )}

      {isInline && (
        <span
          style={{
            fontSize: "0.8125rem",
            fontWeight: 500,
            color: "#64748B",
            fontFamily:
              "'Inter Tight', Inter, system-ui, -apple-system, sans-serif",
          }}
        >
          {label}
        </span>
      )}

      <style>{`
        .logo-loader-path {
          stroke-dasharray: 6000;
          stroke-dashoffset: 6000;
          fill-opacity: 0;
          animation: logoLoaderDraw 2.4s cubic-bezier(0.65, 0, 0.35, 1) infinite;
        }
        .logo-loader-path--accent {
          animation-delay: 0.35s;
        }
        @keyframes logoLoaderDraw {
          0% {
            stroke-dashoffset: 6000;
            fill-opacity: 0;
          }
          55% {
            stroke-dashoffset: 0;
            fill-opacity: 0;
          }
          75% {
            stroke-dashoffset: 0;
            fill-opacity: 1;
          }
          92% {
            stroke-dashoffset: 0;
            fill-opacity: 1;
          }
          100% {
            stroke-dashoffset: 0;
            fill-opacity: 0;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .logo-loader-path {
            animation-duration: 4s;
          }
        }
      `}</style>
    </div>
  );
};

export default LogoLoader;
